import React, { useEffect, useState, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { getStripePublishableKey, createIntent, finalizeSimOrder, buildGooglePayFinalizePayload, isValidOptionalPhone } from '../services/stripeService';

const GOOGLE_PAY_SCRIPT_SRC = 'https://pay.google.com/gp/p/js/pay.js';

/**
 * Google Pay Web API (PaymentsClient) — pay.js only. Stripe as PAYMENT_GATEWAY.
 *
 * Environment controlled ONLY by REACT_APP_GOOGLE_PAY_ENV (no hostname detection).
 * - TEST: no merchantId in merchantInfo (only merchantName); safe for testing on live domain.
 * - PRODUCTION: merchantId required. Set REACT_APP_GOOGLE_PAY_ENV=PRODUCTION for live payments.
 */
const GOOGLE_PAY_MERCHANT_ID = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || 'BCR2DN5TV3YKR2SU';

function getGooglePayEnvironment() {
  const env = process.env.REACT_APP_GOOGLE_PAY_ENV || 'TEST';
  return env === 'PRODUCTION' ? 'PRODUCTION' : 'TEST';
}

/** Basic email format validation (guest checkout). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return typeof email === 'string' && EMAIL_REGEX.test(email.trim());
}

export default function GooglePayWebButton({
  totalAmount = 0,
  cartItems = [],
  selectedCurrency = 'USD',
  country = 'US',
  merchantName = 'Roam4Less',
  customerName = '',
  customerEmail = '',
  customerPhone = '',
  onPaymentSuccess,
  onPaymentError,
  validateCartItems,
  /** If false, never show billing modal when name/email empty (e.g. desktop with form above). Default true = show modal (e.g. mobile). */
  showBillingModalWhenEmpty = true,
  affiliateTid = '',
}) {
  const stripe = useStripe();
  const { t } = useTranslation();
  const [paymentsClient, setPaymentsClient] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingDetails, setBillingDetails] = useState({ name: '', email: '', phone: '' });
  const googlePayLoadedRef = useRef(false);
  const paymentSheetOpenRef = useRef(false); // Block double-click and "Another PaymentRequest UI" from concurrent loadPaymentData()
  const environment = getGooglePayEnvironment();

  const billingModalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      transform: 'translate(-50%, -50%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0',
      maxWidth: '500px',
      width: '90%',
      backgroundColor: 'transparent',
      zIndex: 1001,
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
    },
  };

  useEffect(() => {
    if (!window.google || !window.google.payments || !window.google.payments.api) {
      // pay.js loads here (checkout only), not in index.html: Google sets its NID cookie when it downloads.
      if (!document.querySelector(`script[src="${GOOGLE_PAY_SCRIPT_SRC}"]`)) {
        const script = document.createElement('script');
        script.src = GOOGLE_PAY_SCRIPT_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      window.__googlePayLoaded = () => {
        if (window.google?.payments?.api && !googlePayLoadedRef.current) {
          googlePayLoadedRef.current = true;
          initPaymentsClient();
        }
      };
      const checkGooglePay = setInterval(() => {
        if (window.google?.payments?.api) {
          clearInterval(checkGooglePay);
          if (!googlePayLoadedRef.current) {
            googlePayLoadedRef.current = true;
            initPaymentsClient();
          }
        }
      }, 200);
      return () => clearInterval(checkGooglePay);
    }
    if (!googlePayLoadedRef.current) {
      googlePayLoadedRef.current = true;
      initPaymentsClient();
    }

    function initPaymentsClient() {
      try {
        console.log('[Google Pay] Environment:', environment, '(from REACT_APP_GOOGLE_PAY_ENV only, no hostname detection)');
        const client = new window.google.payments.api.PaymentsClient({ environment });
        setPaymentsClient(client);
        setIsReady(true);
      } catch (err) {
        console.error('Google Pay PaymentsClient init failed:', err);
        setIsReady(false);
      }
      setIsLoading(false);
    }
  }, [environment]);

  const buildOrderDataItems = () => {
    return cartItems.map((item) => {
      const baseItem = {
        type:
          item.plan_type === 'Data Bundles'
            ? 'data_bundle'
            : item.plan_type === 'Global eSIM'
              ? 'global_esim'
              : 'unlimited',
        country: item.country_code || item.iso3 || 'USA',
        days: item.days || 30,
        no_of_esims: item.esim_count || 1,
        data_usage_allowance: item.gb || 20,
        payment_amount: parseFloat(item.api_calculated_price || item.price) || 0,
      };
      if (item.plan_type === 'Unlimited Plans' && item.startDate) {
        baseItem.start_date = new Date(item.startDate).getTime().toString();
      }
      return baseItem;
    });
  };

  const getPaymentDataRequest = () => {
    const amountStr = totalAmount.toFixed(2);
    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'],
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'stripe',
              'stripe:version': '2018-10-31',
              'stripe:publishableKey': getStripePublishableKey(),
            },
          },
        },
      ],
      merchantInfo: environment === 'PRODUCTION'
        ? { merchantId: GOOGLE_PAY_MERCHANT_ID, merchantName }
        : { merchantName }, // TEST: do NOT send merchantId (prevents OR_CCREU_02 / live+test mismatch)
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: amountStr,
        currencyCode: selectedCurrency,
        countryCode: country,
      },
    };
  };

  const isBillingValid = () =>
    billingDetails.name.trim() !== '' &&
    billingDetails.email.trim() !== '' &&
    billingDetails.email.includes('@') &&
    isValidOptionalPhone(billingDetails.phone);

  const handleBillingInputChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleBillingCancel = () => {
    setShowBillingModal(false);
    setBillingDetails({ name: '', email: '', phone: '' });
  };

  const handleBillingSubmit = (e) => {
    e.preventDefault();
    if (!isBillingValid()) {
      if (!billingDetails.name.trim()) onPaymentError(new Error(t('paymentForm.errors.nameRequired') || 'Full name is required.'));
      else if (!billingDetails.email.trim() || !billingDetails.email.includes('@')) onPaymentError(new Error(t('paymentForm.errors.emailInvalid') || 'Please enter a valid email address.'));
      else onPaymentError(new Error(t('paymentForm.errors.phoneInvalid')));
      return;
    }
    setShowBillingModal(false);
    runGooglePayPayment({ name: billingDetails.name.trim(), email: billingDetails.email.trim(), phone: billingDetails.phone.trim() });
  };

  /**
   * Single payment lifecycle (matches Apple Pay):
   * 1) createIntent → store clientSecret + paymentIntentId
   * 2) Google Pay sheet → extract token
   * 3) Confirm PaymentIntent on frontend with Stripe
   * 4) Only if payment succeeded → finalizeSimOrder (no payment_token)
   * @param {Object} [billingOverride] - Optional { name, email, phone } from billing modal (e.g. on mobile)
   */
  const runGooglePayPayment = async (billingOverride) => {
    if (!paymentsClient || !stripe || totalAmount <= 0) return;
    if (validateCartItems && !validateCartItems()) return;
    if (paymentSheetOpenRef.current) return;

    const name = (billingOverride?.name ?? customerName ?? '').trim();
    const email = (billingOverride?.email ?? customerEmail ?? '').trim();
    const phone = (billingOverride?.phone ?? customerPhone ?? '').trim();

    if (!name) {
      onPaymentError(new Error('Full name is required. Please enter your name above.'));
      return;
    }
    if (!email) {
      onPaymentError(new Error('Email is required. Please enter your email above.'));
      return;
    }
    if (!isValidEmail(email)) {
      onPaymentError(new Error('Please enter a valid email address.'));
      return;
    }
    if (!isValidOptionalPhone(phone)) {
      onPaymentError(new Error(t('paymentForm.errors.phoneInvalid')));
      return;
    }

    paymentSheetOpenRef.current = true;
    setIsProcessing(true);

    // ── FirstPromoter: register lead before payment
    try {
      if (window.fpr && email) {
        window.fpr('track', { type: 'signup', email });
      }
    } catch (e) { /* never block payment */ }

    try {
      const orderItems = buildOrderDataItems();

      // Step 1 – Create intent; store clientSecret and paymentIntentId.
      const { clientSecret } = await createIntent({
        name,
        email,
        phone,
        amount: totalAmount,
        currency: selectedCurrency,
        items: orderItems,
      });

      // Step 2 – Google Pay sheet; extract token.
      const paymentDataRequest = getPaymentDataRequest();
      const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);

      const tokenizationData = paymentData.paymentMethodData?.tokenizationData;
      if (!tokenizationData?.token) {
        throw new Error('No payment token received from Google Pay.');
      }

      const payment_token = tokenizationData.token;
      let tokenId;
      try {
        const tokenObj = JSON.parse(payment_token);
        tokenId = tokenObj?.id || payment_token;
      } catch {
        tokenId = payment_token;
      }
      if (!tokenId) {
        throw new Error('Invalid payment token from Google Pay.');
      }

      // Step 3 – Confirm PaymentIntent on frontend with Stripe.
      const confirmParams = tokenId.startsWith('pm_')
        ? { payment_method: tokenId }
        : { payment_method: { card: { token: tokenId } } };
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, confirmParams);

      if (error) {
        onPaymentError(error);
        return;
      }
      if (paymentIntent?.status !== 'succeeded') {
        onPaymentError(new Error(`Payment status: ${paymentIntent?.status || 'unknown'}. Please try again.`));
        return;
      }

      // Step 4 – Finalize order only after successful payment (same shape as Apple Pay; no payment_token).
      const finalizePayload = buildGooglePayFinalizePayload({
        payment_intent_id: paymentIntent.id,
        items: orderItems,
        name,
        email,
        phone,
        currency: selectedCurrency,
        ...(affiliateTid ? { tid: affiliateTid } : {})
      });
      await finalizeSimOrder(finalizePayload);

      onPaymentSuccess({ id: paymentIntent.id });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.statusMessage ||
        err.message ||
        'Payment failed. Please try again.';
      const errorCode = err.statusCode || err.response?.data?.code || '';
      const isAnotherPaymentRequestUI = /Another PaymentRequest UI|already showing/i.test(message);
      if (isAnotherPaymentRequestUI) {
        onPaymentError(new Error(
          'Google Pay is already open in another tab or window. Close it there, or use only one checkout tab, then try again.'
        ));
        return;
      }
      if (/OR_BIBED/i.test(message) || /OR_BIBED/i.test(errorCode) || /OR_BIBED/i.test(String(err.response?.data))) {
        console.warn(
          '[Google Pay] OR_BIBED_11: Register this domain in Stripe Dashboard → Payment method domains. See docs/TROUBLESHOOTING_GOOGLE_PAY.md'
        );
        onPaymentError(new Error(
          'Google Pay is not set up for this site yet. Please register this domain in Stripe (Payment method domains) or try another payment method.'
        ));
        return;
      }
      if (/OR_CCREU/i.test(message) || /OR_CCREU/i.test(errorCode) || /OR_CCREU/i.test(String(err.response?.data))) {
        console.warn(
          '[Google Pay] OR_CCREU: Use REACT_APP_GOOGLE_PAY_ENV=TEST; try another card or Google account. See docs/TROUBLESHOOTING_GOOGLE_PAY.md'
        );
        onPaymentError(new Error(
          'Google Pay could not complete this payment. Try a different card in Google Pay, or use card payment instead.'
        ));
        return;
      }
      onPaymentError(err instanceof Error ? err : new Error(message));
    } finally {
      paymentSheetOpenRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleClick = () => {
    const hasInlineBilling = (customerName || '').trim() && (customerEmail || '').trim() && isValidEmail(customerEmail);
    if (hasInlineBilling) {
      runGooglePayPayment();
      return;
    }
    // Desktop: parent passes showBillingModalWhenEmpty=false → don't open modal, ask to fill form above.
    if (!showBillingModalWhenEmpty) {
      const msg = !(customerName || '').trim()
        ? (t('paymentForm.errors.nameRequired') || 'Please enter your name above.')
        : (t('paymentForm.errors.emailInvalid') || 'Please enter a valid email address above.');
      if (onPaymentError) onPaymentError(new Error(msg));
      return;
    }
    setShowBillingModal(true);
  };

  if (isLoading) {
    return (
      <div className="google-pay-web-loading" style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
        Loading Google Pay...
      </div>
    );
  }

  if (!stripe || !isReady || !paymentsClient || totalAmount <= 0) {
    return null;
  }

  return (
    <>
      <div
        className="custom-google-pay-web-button"
        onClick={isProcessing ? undefined : handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
            e.preventDefault();
            handleClick();
          }
        }}
        style={{
          cursor: isProcessing ? 'wait' : 'pointer',
          opacity: isProcessing ? 0.7 : 1,
          transition: 'all 0.2s ease',
          display: 'inline-block',
        }}
      >
        <img
          src="/admin/images/plain-button-dark-pill-shape.png"
          alt="Google Pay"
          style={{
            width: '200px',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>

      {/* Billing (name & email) modal – used when inline fields are empty, e.g. on mobile */}
      <Modal
        isOpen={showBillingModal}
        onRequestClose={handleBillingCancel}
        style={billingModalStyles}
        portalClassName="google-pay-billing-modal-portal"
        contentLabel={t('paymentForm.billingInfo') || 'Billing information'}
      >
        <div className="paymentsuccess">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" onClick={handleBillingCancel} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl ? window.getPublicUrl('images/close.svg') : '/images/close.svg'} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12">
                  <h3>{t('paymentForm.billingInfo') || 'Billing information'}</h3>
                  <p className="mb-3">{t('paymentForm.billingInfoDescription') || 'Enter your name and email to continue with Google Pay.'}</p>
                  <form onSubmit={handleBillingSubmit}>
                    <div className="form-group">
                      <label>{t('paymentForm.name') || 'Full Name'} *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder={t('paymentForm.namePlaceholder') || 'Your full name'}
                        value={billingDetails.name}
                        onChange={handleBillingInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('paymentForm.email') || 'Email'} *</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder={t('paymentForm.emailPlaceholder') || 'your@email.com'}
                        value={billingDetails.email}
                        onChange={handleBillingInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('paymentForm.phone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        className="form-control"
                        placeholder={t('paymentForm.phonePlaceholder')}
                        value={billingDetails.phone}
                        onChange={handleBillingInputChange}
                      />
                    </div>
                    <div className="modal-footer border-0 pt-3">
                      <button type="submit" className="view-all get-sim Continue billing-modal-continue-btn" disabled={!isBillingValid()}>
                        {t('paymentForm.continueToPayment') || 'Continue to Google Pay'}
                      </button>
                      <button type="button" className="btn view-all get-sim billing-modal-cancel-btn" onClick={handleBillingCancel}>
                        {t('paymentForm.cancel') || 'Cancel'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
